import { useState, useEffect, useRef } from 'react';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Récents' },
  { value: 'name', label: 'A → Z' },
  { value: 'distance', label: 'Distance' },
];

const COUNTRIES = [
  'France',
  'Belgique',
  'Suisse',
  'Royaume-Uni',
  'Allemagne',
  'Pays-Bas',
  'États-Unis',
  'Canada',
  'Autre',
];

/**
 * Sidebar de filtres (refonte annuaire). Desktop : colonne sticky.
 * Mobile : bottom-sheet déclenchée par un bouton fixe « Filtres (N) ».
 * La logique (debounce texte, géoloc, reset) est inchangée ; seuls le
 * rendu et la présentation des genres / tri (en tags) évoluent.
 */
export default function ArtistFilters({ filters, onFilterChange, genres, onReset }) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [hasGeolocation, setHasGeolocation] = useState(false);

  useEffect(() => {
    if (navigator.geolocation) setHasGeolocation(true);
  }, []);

  // Fermer la bottom-sheet à l'Échap
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSheetOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen]);

  // Debounce des champs texte : un timer par champ, persisté dans un ref.
  const debounceTimersRef = useRef({});
  useEffect(() => {
    const timers = debounceTimersRef.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  const debouncedFilterChange = (field, value) => {
    clearTimeout(debounceTimersRef.current[field]);
    debounceTimersRef.current[field] = setTimeout(() => {
      handleFilterChange(field, value);
    }, 500);
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  // Toggle d'un genre (single-select côté API : un slug ou rien)
  const toggleGenre = (slug) => {
    handleFilterChange('genre', localFilters.genre === slug ? '' : slug);
  };

  const handleGeolocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newFilters = { ...localFilters, latitude, longitude, sort: 'distance' };
        setLocalFilters(newFilters);
        onFilterChange(newFilters);
      },
      () => {
        // Échec silencieux : permission refusée
      }
    );
  };

  const handleReset = () => {
    const emptyFilters = {
      search: '',
      genre: '',
      city: '',
      country: '',
      availableFrom: '',
      availableTo: '',
      sort: 'newest',
      latitude: null,
      longitude: null,
    };
    setLocalFilters(emptyFilters);
    onFilterChange(emptyFilters);
    if (onReset) onReset();
  };

  // Nombre de filtres actifs (pour le badge mobile)
  const activeCount = [
    filters.search,
    filters.genre,
    filters.city,
    filters.country,
    filters.availableFrom,
    filters.availableTo,
  ].filter(Boolean).length + (filters.latitude != null ? 1 : 0);

  const tagBase =
    'font-mono text-[9.5px] uppercase tracking-[0.14em] px-2.5 py-1 border transition-colors duration-150 cursor-pointer';
  const tagOff = 'th-border th-text-muted hover:th-text';
  const tagOn = 'border-accent text-accent';

  const groupTitle =
    'font-mono text-[10px] uppercase tracking-[0.22em] th-text pb-2 mb-3 border-b th-border';

  // Bloc de filtres réutilisé en sidebar (desktop) et en bottom-sheet (mobile)
  const groups = (
    <>
      <div className="mb-7">
        <div className={groupTitle}>Recherche</div>
        <input
          type="text"
          defaultValue={filters.search || ''}
          onChange={(e) => debouncedFilterChange('search', e.target.value)}
          placeholder="Nom d'artiste..."
          className="th-input"
        />
      </div>

      <div className="mb-7">
        <div className={groupTitle}>Genres</div>
        <div className="flex flex-wrap gap-1.5">
          {genres.map((genre) => (
            <button
              key={genre.id}
              type="button"
              onClick={() => toggleGenre(genre.slug)}
              className={`${tagBase} ${filters.genre === genre.slug ? tagOn : tagOff}`}
            >
              {genre.name}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-7">
        <div className={groupTitle}>Ville</div>
        <input
          type="text"
          defaultValue={filters.city || ''}
          onChange={(e) => debouncedFilterChange('city', e.target.value)}
          placeholder="Paris, Lyon..."
          className="th-input mb-3"
        />
        {hasGeolocation && (
          <button
            type="button"
            onClick={handleGeolocation}
            className="th-btn w-full text-[10px]"
            title="Utiliser ma localisation actuelle"
          >
            ◎ Autour de moi
          </button>
        )}
      </div>

      <div className="mb-7">
        <div className={groupTitle}>Pays</div>
        <select
          value={filters.country || ''}
          onChange={(e) => handleFilterChange('country', e.target.value)}
          className="th-input"
        >
          <option value="">Tous</option>
          {COUNTRIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-7">
        <div className={groupTitle}>Disponible entre</div>
        <label className="block font-mono text-[9.5px] uppercase tracking-[0.2em] th-text-muted mb-1">
          Du
        </label>
        <input
          type="date"
          value={filters.availableFrom || ''}
          onChange={(e) => handleFilterChange('availableFrom', e.target.value)}
          className="th-input mb-3"
        />
        <label className="block font-mono text-[9.5px] uppercase tracking-[0.2em] th-text-muted mb-1">
          Au
        </label>
        <input
          type="date"
          value={filters.availableTo || ''}
          onChange={(e) => handleFilterChange('availableTo', e.target.value)}
          className="th-input"
        />
      </div>

      <div className="mb-7">
        <div className={groupTitle}>Tri</div>
        <div className="flex flex-wrap gap-1.5">
          {SORT_OPTIONS.filter((o) => o.value !== 'distance' || hasGeolocation).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleFilterChange('sort', opt.value)}
              className={`${tagBase} ${
                (filters.sort || 'newest') === opt.value ? tagOn : tagOff
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={handleReset}
        className="th-link-mono"
      >
        Réinitialiser les filtres ×
      </button>
    </>
  );

  return (
    <>
      {/* Desktop : sidebar sticky */}
      <aside className="hidden lg:block lg:sticky lg:top-6 self-start border-r th-border pr-6">
        {groups}
      </aside>

      {/* Mobile : bouton fixe « Filtres (N) » */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-30 p-4 th-bg border-t th-border">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="th-btn-accent w-full"
        >
          Filtres{activeCount > 0 ? ` (${activeCount})` : ''}
        </button>
      </div>

      {/* Mobile : bottom-sheet */}
      {sheetOpen && (
        <div className="lg:hidden fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Filtres">
          <div
            className="absolute inset-0 bg-black/55"
            onClick={() => setSheetOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto th-bg-elevated border-t th-border p-5 pb-24">
            <div className="w-9 h-[3px] th-bg-surface mx-auto mb-5" />
            {groups}
            <div className="fixed inset-x-0 bottom-0 z-50 p-4 th-bg-elevated border-t th-border">
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="th-btn-accent w-full"
              >
                Voir les résultats
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
